<template>
  <div class="employee-manager">
    <v-row v-if="userService.hasRight('g_employeemanager')">
      <employee-list class="mb-4" :visible="showEmployeeDashboard" @close="showEmployeeDashboard = false"></employee-list>
      <v-col align="center" cols="12">
        <v-btn @click="showEmployeeDashboard = true" class="primary">Status von Rechten & (TAC) Mitgliedschaften</v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col align="center" class="mb-5" cols="12">
        <v-btn class="mx-2" @click="downloadBLSList">BLS-AED Liste herunterladen</v-btn>
        <v-btn class="mx-2" @click="downloadBLSListFileZip">BLS-AED Ausweise ZIP</v-btn>
        <v-btn class="mx-2" color="secondary" @click="downloadEmployeeCSV">
          <v-icon left>mdi-file-excel</v-icon>
          Mitarbeiterliste (CSV)
        </v-btn>
      </v-col>
    </v-row>

    <v-expansion-panels dark>
      <v-expansion-panel v-for="(studio, index) in studios" :key="index">
        <v-expansion-panel-header>
          {{ studio.name }}
          <span class="orange--text ml-2">
            {{
              studio.studio && storeStudios.find((std) => std._id === studio.studio)
                ? " Center (" + storeStudios.find((std) => std._id === studio.studio).name + ")"
                : ""
            }}
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pt-4">
          <v-row v-if="employee.length" class="mb-4">
            <v-col cols="auto">
              <v-card-title class="pb-0 justify-content-center text-body-2">Anzahl Mitarbeiter</v-card-title>
              <v-card-title class="pt-1 justify-content-center font-weight-bold text-h6">
                {{ employeeForStudio(studio).length }}
              </v-card-title>
            </v-col>
            <v-col cols="auto">
              <v-card-title class="pb-0 justify-content-center text-body-2">Anzahl Stellenprozent</v-card-title>
              <v-card-title class="pt-1 justify-content-center font-weight-bold text-h6">
                {{ employeeForStudio(studio).reduce((acc, curr) => acc + (Number((getEmployeeStudioData(curr, studio) || {}).percentage) || 0), 0) }}%
              </v-card-title>
            </v-col>
            <v-col cols="auto">
              <v-card
                hover
                @click="
                  () => {
                    studio.code ? (targetHoursStudio = studio) : (targetHoursTeam = studio)
                  }
                "
                elevation-0
                color="transparent">
                <v-card-title class="pb-0 justify-content-center text-body-2"> Soll Std. {{ $moment().format("MMM YY") }} </v-card-title>
                <v-card-title class="pt-1 justify-content-center font-weight-bold text-h6">
                  {{ Math.round((calcBusinessDays - vacationDaysForMonth(studio).length) * 8.4 * 1000) / 1000 + " h" }}
                </v-card-title>
              </v-card>
            </v-col>
            <v-col cols="auto" style="align-self: center">
              <v-btn icon @click="deleteTeam(studio._id)" title="Team löschen">
                <v-icon color="red lighten-1">mdi-delete-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-6"></v-divider>

          <v-row>
            <v-col style="margin-top: 60px" cols="12" md="6" lg="4" xl="3">
              <v-card
                hover
                class="black rounded-xl employee-manager__card d-flex fill-height flex-column justify-content-center align-center"
                @click="openAddEmployeeDialog(studio)"
                aria-label="Neuen Mitarbeiter hinzufügen">
                <v-avatar class="employee-manager__card-avatar" color="grey darken-2" size="125">
                  <v-icon large color="white">mdi-plus-circle-outline</v-icon>
                </v-avatar>
                <v-card-title class="pt-12 white--text text-subtitle-1">Mitarbeiter hinzufügen</v-card-title>
              </v-card>
            </v-col>

            <v-col
              style="margin-top: 65px"
              v-for="employeItem of employeeForStudio(studio)"
              :key="'employe' + studio.name + employeItem._id"
              cols="12"
              md="6"
              lg="4"
              xl="3">
              <v-card
                @click="openEditEmployeeDialog(employeItem, studio)"
                hover
                class="black employee-manager__card d-flex flex-column fill-height rounded-xl justify-space-between align-center"
                :aria-label="`Mitarbeiter ${employeItem.fullName} bearbeiten`">
                <v-avatar
                  class="employee-manager__card-avatar"
                  :class="{
                    'gold-border': getEmployeeStudioData(employeItem, studio)?.border === 'gold',
                    'blue-border': getEmployeeStudioData(employeItem, studio)?.border === 'blue',
                    'green-border': getEmployeeStudioData(employeItem, studio)?.border === 'green',
                  }"
                  color="grey darken-1"
                  size="125">
                  <img
                    v-if="employeItem.picture"
                    :src="'/img/profilbilder' + employeItem.picture"
                    :alt="`Profilbild von ${employeItem.fullName}`"
                    style="object-fit: cover; width: 100%; height: 100%" />
                  <v-icon v-else x-large color="white">mdi-account-circle-outline</v-icon>
                </v-avatar>

                <v-card-title class="white--text justify-center text-center px-2 text-subtitle-1" style="word-break: break-word; line-height: 1.3em">
                  {{ employeItem.fullName }}
                </v-card-title>
                <div class="text-center pb-4 px-1 chip-container-on-card" style="line-height: 1.8; max-width: 100%">
                  <v-chip
                    v-if="getEmployeeStudioData(employeItem, studio)?.title"
                    small
                    label
                    outlined
                    color="blue-grey lighten-3"
                    class="ma-1 employee-info-chip">
                    <v-icon left small>mdi-tag-outline</v-icon>
                    {{ getEmployeeStudioData(employeItem, studio).title }}
                  </v-chip>

                  <v-chip
                    v-if="typeof getEmployeeStudioData(employeItem, studio)?.percentage === 'number'"
                    small
                    label
                    outlined
                    color="teal lighten-2"
                    class="ma-1 employee-info-chip">
                    <v-icon left small>mdi-chart-pie</v-icon>
                    Statistik: {{ getEmployeeStudioData(employeItem, studio).percentage }}%
                  </v-chip>

                  <v-chip
                    v-if="!employeItem.noFixPercentage && getPercentageForEmployee(employeItem, studio) > 0"
                    small
                    label
                    outlined
                    color="amber darken-2"
                    class="ma-1 employee-info-chip">
                    <v-icon left small>mdi-clock-check-outline</v-icon>
                    Zeiterfassung: {{ getPercentageForEmployee(employeItem, studio) }}%
                  </v-chip>

                  <v-chip v-if="employeItem.noFixPercentage" small label outlined color="blue-grey darken-2" class="ma-1 employee-info-chip">
                    <v-icon left small>mdi-timer-sand-empty</v-icon>
                    Zeiterfassung: Flexibel
                  </v-chip>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header>
          Neues Team hinzufügen
          <v-icon style="justify-content: left" color="primary" class="ml-2">mdi-plus-circle-outline</v-icon>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="pt-4">
          <v-text-field label="Titel des Teams" v-model="newTeam.name" outlined dense></v-text-field>
          <v-select
            label="Sichtbar für Studio (Center)"
            :items="
              storeStudios
                .filter((s) => s.code)
                .map((s) => {
                  return { text: s.name, value: s._id }
                })
            "
            v-model="newTeam.studio"
            outlined
            dense></v-select>
          <v-btn color="primary" @click="createNewTeam()">Erstellen</v-btn>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="editEmployeeDialog" persistent max-width="850px" :fullscreen="$vuetify.breakpoint.mobile" scrollable>
      <v-card v-if="selectedEmployeeForEdit && currentStudioContext" dark>
        <v-system-bar color="grey darken-3" window dark>
          <v-icon>mdi-account-edit</v-icon>
          <span class="ml-2">{{ selectedEmployeeForEdit.fullName }} bearbeiten ({{ currentStudioContext.name }})</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeEditEmployeeDialog" aria-label="Schliessen">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-system-bar>

        <v-card-text class="pt-6">
          <v-container fluid>
            <v-row>
              <v-col cols="12" md="4" class="text-center mb-4 mb-md-0">
                <div style="position: relative; display: inline-block">
                  <v-avatar
                    class="employee-manager__card-avatar-dialog"
                    :class="{
                      'gold-border': selectedEmployeeStudioData.border === 'gold',
                      'blue-border': selectedEmployeeStudioData.border === 'blue',
                      'green-border': selectedEmployeeStudioData.border === 'green',
                    }"
                    color="grey darken-2"
                    size="135">
                    <slim-cropper v-if="showSlimCropper" :options="slimOptions" :style="{ width: '135px', height: (135 * 92) / 83 + 'px' }">
                      <img
                        v-if="selectedEmployeeForEdit.picture"
                        :src="'/img/profilbilder' + selectedEmployeeForEdit.picture"
                        alt="Profilbild Vorschau" />
                      <input type="file" accept="image/*" name="slim[]" />
                    </slim-cropper>
                    <img
                      v-else-if="selectedEmployeeForEdit.picture"
                      :src="'/img/profilbilder' + selectedEmployeeForEdit.picture"
                      alt="Profilbild"
                      style="object-fit: cover; width: 100%; height: 100%" />
                    <v-icon v-else size="70" color="grey lighten-2">mdi-account-circle</v-icon>
                  </v-avatar>
                  <v-btn
                    fab
                    x-small
                    depressed
                    color="primary"
                    @click="showSlimCropper = !showSlimCropper"
                    title="Bild ändern/abbrechen"
                    style="position: absolute; bottom: 0px; right: 0px">
                    <v-icon>{{ showSlimCropper ? "mdi-close-circle-outline" : "mdi-pencil-outline" }}</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <v-col cols="12" md="8">
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field label="Angezeigter Name" v-model="selectedEmployeeForEdit.fullName" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field label="E-Mail (Login)" v-model="selectedEmployeeForEdit.userEmail" outlined dense disabled readonly></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field label="Titel im Studio/Team" v-model="selectedEmployeeStudioData.title" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      outlined
                      dense
                      v-model="selectedEmployeeStudioData.border"
                      :items="borderPossibilities"
                      label="Rahmen (Avatar)"></v-select>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>
            <p class="text-overline grey--text text--lighten-1">BLS-AED Informationen</p>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="BLS-AED gemacht am"
                  v-model="selectedEmployeeForEdit.blsAed"
                  outlined
                  dense
                  placeholder="TT.MM.JJJJ"
                  hide-details></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" md="8" class="d-flex align-center flex-wrap">
                <input
                  type="file"
                  :ref="'fileInputEdit' + selectedEmployeeForEdit._id"
                  style="display: none"
                  @change="onBLSFileChange($event, selectedEmployeeForEdit)"
                  accept=".pdf,.jpg,.jpeg,.png" />

                <v-btn
                  small
                  outlined
                  color="blue lighten-2"
                  class="mr-2 my-1"
                  @click="triggerBLSFileInput($event, selectedEmployeeForEdit, 'fileInputEdit')">
                  <v-icon left small>mdi-upload-outline</v-icon> Zertifikat hochladen
                </v-btn>

                <v-btn
                  small
                  outlined
                  class="mr-3 my-1"
                  @click="downloadBLSAEDFile(selectedEmployeeForEdit.blsAedFile)"
                  :disabled="!selectedEmployeeForEdit.blsAedFile"
                  title="Zertifikat ansehen">
                  <v-icon left small>mdi-download-outline</v-icon> Ansehen
                </v-btn>

                <div class="d-flex align-center my-1">
                  <template v-if="selectedEmployeeForEdit.blsAedFile">
                    <v-icon color="green lighten-1" small class="mr-1">mdi-check-circle</v-icon>
                    <span class="text-caption green--text text--lighten-2 font-weight-medium"> Datei vorhanden </span>
                  </template>
                  <template v-else>
                    <v-icon color="orange lighten-1" small class="mr-1">mdi-alert-circle-outline</v-icon>
                    <span class="text-caption orange--text text--lighten-2"> Keine Datei hochgeladen </span>
                  </template>
                </div>
              </v-col>
            </v-row>
            <v-divider class="my-5"></v-divider>
            <p class="text-overline grey--text text--lighten-1">Centerleitung</p>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-checkbox
                  label="Centerleitung"
                  dense
                  @change="forceUpdateCenterManager()"
                  v-model="selectedEmployeeStudioData.centerManager"></v-checkbox>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="8"
                v-if="
                  selectedEmployeeStudioData.centerManager &&
                  !(selectedEmployeeForEdit.userEmail.includes('wellcomefit') || selectedEmployeeForEdit.userEmail.includes('wintifit'))
                ">
                <v-text-field
                  label="Offizielle Centerleiter E-Mail"
                  v-model="selectedEmployeeStudioData.centerManagerEmail"
                  outlined
                  dense
                  type="email"></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>
            <p class="text-overline grey--text text--lighten-1">Arbeitszeit & Planung</p>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Eintrittsdatum"
                  v-model="selectedEmployeeForEdit.entryDate"
                  type="date"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Austrittsdatum"
                  v-model="selectedEmployeeForEdit.exitDate"
                  type="date"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Ferienanspruch pro Jahr (Tage)"
                  v-model="selectedEmployeeForEdit.yearlyVacationDays"
                  type="number"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours')"></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Stellenprozent (Statistik)"
                  v-model="selectedEmployeeStudioData.percentage"
                  type="number"
                  suffix="%"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_set_percentage')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-if="!selectedEmployeeForEdit.noFixPercentage"
                  label="Aktuelles Pensum (Zeiterfassung)"
                  :value="activePensumOverride ? activePensumOverride.percentage : selectedEmployeeForEdit.timeTrackerPercentage"
                  @input="
                    (val) => {
                      if (!activePensumOverride) selectedEmployeeForEdit.timeTrackerPercentage = val
                    }
                  "
                  type="number"
                  suffix="%"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours') || hasActivePensumOverride"
                  :hint="
                    hasActivePensumOverride
                      ? `Gesteuert durch Pensum-Historie (Base: ${selectedEmployeeForEdit.timeTrackerPercentage}%)`
                      : 'Standardwert für dieses Studio'
                  "
                  :persistent-hint="hasActivePensumOverride"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-if="!selectedEmployeeForEdit.noFixPercentage"
                  label="Überstunden Saldo"
                  v-model="selectedEmployeeForEdit.overTime"
                  type="number"
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Ferienstunden Saldo"
                  v-model="selectedEmployeeStudioData.vacationHours"
                  type="number"
                  :suffix="
                    selectedEmployeeStudioData.vacationHours
                      ? ` (${
                          Math.round(
                            (selectedEmployeeStudioData.vacationHours /
                              ((8.4 / 100) * getPercentageForEmployee(selectedEmployeeForEdit, currentStudioContext))) *
                              100,
                          ) / 100
                        } Tage)`
                      : ''
                  "
                  outlined
                  dense
                  :disabled="!userService.hasRight('tt_targetHours')">
                  <template v-slot:append>
                    <v-tooltip top v-if="calculatedVacationDays">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          color="primary"
                          class="mt-1"
                          v-bind="attrs"
                          v-on="on"
                          @click="applyCalculatedVacation"
                          :disabled="!userService.hasRight('tt_targetHours')">
                          <v-icon>mdi-calculator</v-icon>
                        </v-btn>
                      </template>
                      <span>Autom. Berechnung anwenden ({{ calculatedVacationDays }} Tage)</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  label="Sortierreihenfolge"
                  v-model="selectedEmployeeStudioData.sort"
                  type="number"
                  outlined
                  dense
                  hint="Niedrigere Zahl = weiter oben"></v-text-field>
              </v-col>
            </v-row>
            <v-row dense v-if="userService.hasRight('tt_targetHours')">
              <v-col cols="12">
                <p class="text-caption grey--text mb-1">Pensum-Historie (Für Änderungen im Arbeitspensum)</p>
                <div v-for="(hist, index) in selectedEmployeeStudioData.pensumHistory" :key="index" class="d-flex align-center mb-2">
                  <v-text-field v-model="hist.percentage" label="Pensum (%)" type="number" dense outlined hide-details class="mr-2"></v-text-field>
                  <v-text-field v-model="hist.validFrom" label="Gültig ab (YYYY-MM)" dense outlined hide-details class="mr-2"></v-text-field>
                  <v-btn icon color="error" @click="selectedEmployeeStudioData.pensumHistory.splice(index, 1)"><v-icon>mdi-delete</v-icon></v-btn>
                </div>
                <v-btn
                  small
                  color="primary"
                  outlined
                  @click="
                    if (!selectedEmployeeStudioData.pensumHistory) $set(selectedEmployeeStudioData, 'pensumHistory', [])
                    selectedEmployeeStudioData.pensumHistory.push({
                      percentage: selectedEmployeeForEdit.timeTrackerPercentage || 100,
                      validFrom: $moment().format('YYYY-MM'),
                    })
                  ">
                  <v-icon left>mdi-plus</v-icon> Historie Eintrag
                </v-btn>
                <div class="text-caption grey--text mt-1">Gültig ab (YYYY-MM) bestimmt, in welchem Monat das abweichende Pensum angewendet wird.</div>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" sm="auto">
                <v-checkbox dense label="Zeiterfassung ohne fixes Pensum" v-model="selectedEmployeeForEdit.noFixPercentage"></v-checkbox>
              </v-col>
              <v-col cols="12" sm="auto" v-if="selectedEmployeeForEdit.studio && selectedEmployeeForEdit.studio.length > 1">
                <v-checkbox dense label="Hauptstudio (Zeiterfassung)" v-model="selectedEmployeeStudioData.mainStudio"></v-checkbox>
              </v-col>
              <v-col cols="12" sm="auto" v-if="getEmployeeStudioData(selectedEmployeeForEdit, currentStudioContext)?.team">
                <v-checkbox
                  dense
                  label="Kann sich selbst einchecken"
                  v-model="selectedEmployeeForEdit.canSelfCheckin"
                  :disabled="!userService.hasRight('tt_targetHours')"></v-checkbox>
              </v-col>
              <v-col cols="12" sm="auto" v-if="selectedEmployeeForEdit.canSelfCheckin && userService.hasRight('tt_targetHours')">
                <v-checkbox
                  dense
                  label="Zweite Firma"
                  v-model="selectedEmployeeForEdit.secondCompany"
                  :disabled="!userService.hasRight('tt_targetHours')"></v-checkbox>
              </v-col>

              <v-col cols="12" sm="auto">
                <v-checkbox dense label="Mitarbeiter verstecken" v-model="selectedEmployeeStudioData.hide"></v-checkbox>
              </v-col>
              <v-col cols="12" sm="auto" v-if="selectedEmployeeStudioData.hide">
                <v-checkbox dense label="Trotzdem im Schichtplan anzeigen" v-model="selectedEmployeeStudioData.showInCalendarAnyway"></v-checkbox>
              </v-col>
            </v-row>

            <div
              v-if="
                selectedEmployeeForEdit.studio &&
                selectedEmployeeForEdit.studio.length > 1 &&
                otherStudiosForEmployee(selectedEmployeeForEdit, currentStudioContext).length > 0
              "
              class="mt-4">
              <p class="text-overline grey--text text--lighten-1">Zugehörigkeit in anderen Teams/Studios</p>
              <v-chip
                small
                class="mr-2 mb-2"
                v-for="std in otherStudiosForEmployee(selectedEmployeeForEdit, currentStudioContext)"
                :key="std._id || std.team"
                :color="std.mainStudio ? 'blue-grey lighten-1' : 'grey darken-2'"
                outlined
                label>
                <v-icon left small v-if="std.mainStudio">mdi-star-circle-outline</v-icon>
                {{ getStudioOrTeamName(std) }}
              </v-chip>
            </div>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-row no-gutters class="pa-4">
          <v-col class="my-1" cols="auto">
            <v-btn outlined color="red lighten-1" @click="confirmDeleteEmployee(selectedEmployeeForEdit, currentStudioContext._id)">
              <v-icon left>mdi-delete-outline</v-icon>
              Aus Team entfernen
            </v-btn>
            <v-spacer></v-spacer>
          </v-col>
          <v-col class="my-1" cols="auto">
            <v-btn outlined @click="closeEditEmployeeDialog" class="mr-2">Abbrechen</v-btn>
          </v-col>
          <v-col class="my-1" cols="auto">
            <v-btn color="primary" depressed @click="saveEditedEmployee">
              <v-icon left>mdi-content-save</v-icon>
              Speichern
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-dialog v-model="addEmployeeDialog" persistent max-width="500px" scrollable>
      <v-card v-if="currentStudioForAdd" dark>
        <v-system-bar color="grey darken-3" window dark>
          <v-icon>mdi-account-plus-outline</v-icon>
          <span class="ml-2">Neuen Mitarbeiter für {{ currentStudioForAdd.name }} hinzufügen</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeAddEmployeeDialog" aria-label="Schliessen">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-system-bar>
        <v-card-text class="pt-6">
          <v-container>
            <user-search
              label="Mitarbeiter suchen..."
              @update="pickUserForAdd"
              class="mb-5"
              dark
              style="display: unset !important"
              outlined
              :disabled="!!newEmployeeData._id"
              v-if="!newEmployeeData._id"></user-search>

            <div v-if="newEmployeeData._id" class="mt-3">
              <p>
                Ausgewählter Benutzer: <strong>{{ newEmployeeData.fullName }}</strong>
              </p>
              <p v-if="newEmployeeData.userEmail" class="text-caption grey--text">({{ newEmployeeData.userEmail }})</p>
              <p class="caption mt-4">
                Klicken Sie auf "Hinzufügen", um {{ newEmployeeData.fullName }} zu {{ currentStudioForAdd.name }} hinzuzufügen. Weitere Details können
                nach dem Speichern im Bearbeiten-Modus des Mitarbeiters angepasst werden.
              </p>
            </div>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-btn
            v-if="newEmployeeData._id"
            text
            small
            color="orange darken-2"
            @click="
              () => {
                newEmployeeData = getInitialNewEmployeeData()
              }
            ">
            Auswahl zurücksetzen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined @click="closeAddEmployeeDialog" class="mr-2">Abbrechen</v-btn>
          <v-btn color="primary" depressed @click="saveNewEmployee" :disabled="!newEmployeeData._id" :loading="isSavingNewEmployee">
            <v-icon left>mdi-account-plus-outline</v-icon>
            Hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" color="green darken-1" timeout="3000" app top right>
      <v-icon left>mdi-check-circle-outline</v-icon>
      Aktion erfolgreich!
    </v-snackbar>
    <studio-dashboard-employee-target-hours-dialog
      v-if="targetHoursStudio || targetHoursTeam"
      :studio="targetHoursStudio"
      :team="targetHoursTeam"
      :allStudios="studios.filter((std) => std.code)"
      @closeDialog="
        () => {
          targetHoursStudio = null
          targetHoursTeam = null
        }
      "></studio-dashboard-employee-target-hours-dialog>
  </div>
</template>

<script>
// ... (Imports bleiben gleich) ...
import api from "../../../api"
import { mapState } from "vuex" // mapActions, mapGetters nicht verwendet
import userService from "../../../services/userService"
import userSearch from "../../../components/userSearch.vue"
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import studioDashboardEmployeeTargetHoursDialog from "./studioDashboardEmployeeTargetHoursDialog.vue"
import employeeList from "@/views/dashboard/employee/employeeList.vue"

export default {
  name: "studioDashboardEmployee",
  props: {
    studios: {
      type: Array,
      required: true,
    },
  },
  components: {
    employeeList,
    userSearch,
    "slim-cropper": Slim,
    studioDashboardEmployeeTargetHoursDialog,
  },
  data() {
    return {
      userService,
      showEmployeeDashboard: false,
      targetHoursStudio: null,
      targetHoursTeam: null,
      employee: [],
      newTeam: {
        name: "",
        targetHours: [],
        studio: null,
      },
      snackbar: false,

      editEmployeeDialog: false,
      selectedEmployeeForEdit: null,
      selectedEmployeeStudioData: {},
      currentStudioContext: null,
      showSlimCropper: false, // Wird jetzt beim Öffnen des Dialogs auf true gesetzt

      addEmployeeDialog: false,
      newEmployeeData: this.getInitialNewEmployeeData(),
      currentStudioForAdd: null,
      isSavingNewEmployee: false, // Für Ladezustand des Buttons

      borderPossibilities: [
        { text: "Gold", value: "gold" },
        { text: "Grün", value: "green" },
        { text: "Blau", value: "blue" },
        { text: "Kein Rahmen", value: null },
      ],
    }
  },
  async created() {
    if (!this.employee.length && this.studios && this.studios.length > 0) {
      // Sicherstellen, dass Studios vorhanden sind
      await this.fetchEmployees()
    } else if (!this.studios || this.studios.length === 0) {
      this.$emit("finishedloading") // Falls keine Studios da sind, trotzdem Ladezustand beenden
    }
  },

  watch: {
    async studios(newVal, oldVal) {
      const newIds = newVal ? newVal.map((s) => s._id).join(",") : ""
      const oldIds = oldVal ? oldVal.map((s) => s._id).join(",") : ""
      if (newIds !== oldIds || (newVal && oldVal && newVal.length !== oldVal.length) || (!oldVal && newVal && newVal.length > 0)) {
        await this.fetchEmployees()
      }
    },
  },
  methods: {
    downloadEmployeeCSV() {
      // CSV Header definieren
      let csvContent = "Center / Team;Name;E-Mail;Funktion\n"

      this.studios.forEach((studio) => {
        // 1. Den vollen Namen des Studios/Teams zusammenbauen
        let fullStudioName = studio.name || ""

        if (studio.studio) {
          const parentCenter = this.storeStudios.find((std) => std._id === studio.studio)
          if (parentCenter) {
            fullStudioName += ` Center (${parentCenter.name})`
          }
        }

        const safeStudioName = fullStudioName.replace(/"/g, '""')

        // 2. NEU: Center-Account (Fitness Team) hinzufügen
        // Wir suchen das aktuelle Studio im storeStudios, um an die .email Eigenschaft zu kommen
        const storeStudio = this.storeStudios.find((std) => std._id === studio._id)

        if (storeStudio && storeStudio.email) {
          const centerEmail = storeStudio.email

          // Wir wenden den gleichen @wellcomefit.ch Filter zur Sicherheit auch hier an
          if (centerEmail.toLowerCase().includes("@wellcomefit.ch") || centerEmail.toLowerCase().includes("@wintifit.ch")) {
            const centerName = `Fitness Team ${storeStudio.name}`.replace(/"/g, '""')
            const centerTitle = "Fitness Team"

            // Center Account als erste Zeile für dieses Studio hinzufügen
            csvContent += `"${safeStudioName}";"${centerName}";"${centerEmail}";"${centerTitle}"\n`
          }
        }

        // 3. Mitarbeiter für dieses Studio holen
        const employeesInStudio = this.employeeForStudio(studio)

        employeesInStudio.forEach((emp) => {
          const empEmail = emp.userEmail || emp.email || ""

          // FILTER: Nur Mitarbeiter mit @wellcomefit.ch E-Mail-Adresse
          if (empEmail.toLowerCase().includes("@wellcomefit.ch")) {
            const empName = emp.fullName ? emp.fullName.replace(/"/g, '""') : ""

            // Spezifische Daten (Titel) abrufen
            const studioData = this.getEmployeeStudioData(emp, studio) || {}
            const title = studioData.title ? studioData.title.replace(/"/g, '""') : ""

            // Zeile zum CSV-String hinzufügen
            csvContent += `"${safeStudioName}";"${empName}";"${empEmail}";"${title}"\n`
          }
        })
      })

      // Blob erstellen und Download triggern
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" })
      const link = document.createElement("a")
      const url = URL.createObjectURL(blob)

      link.setAttribute("href", url)
      link.setAttribute("download", `Interne_Mitarbeiterliste_wellcomefit_${this.$moment().format("YYYY-MM-DD")}.csv`)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$toast.success("CSV-Datei inkl. Center-Accounts wurde erfolgreich generiert.")
    },
    forceUpdateCenterManager() {
      this.$forceUpdate()
    },
    getInitialNewEmployeeData() {
      // Minimal-Struktur für einen noch nicht ausgewählten User
      return {
        _id: null, // Wird durch User-Auswahl (pickedUser._id) gesetzt
        userId: null, // Wird ebenfalls durch User-Auswahl (pickedUser._id) gesetzt
        userEmail: "", // Vom pickedUser
        email: "", // Vom pickedUser
        fullName: "", // Vom pickedUser
        picture: null, // Vom pickedUser
      }
    },

    async fetchEmployees() {
      this.$emit("loading")
      if (this.studios && this.studios.length > 0) {
        try {
          const studioIds = this.studios.map((studio) => studio._id).filter((id) => id)
          if (studioIds.length > 0) {
            this.employee = await api.fetchAllEmployee(studioIds)
          } else {
            this.employee = []
          }
        } catch (error) {
          console.error("Fehler beim Laden der Mitarbeiter:", error)
          this.employee = []
          this.$toast.error("Mitarbeiter konnten nicht geladen werden.")
        }
      } else {
        this.employee = []
      }
      this.$emit("finishedloading")
    },
    getEmployeeStudioData(employee, studioContext) {
      if (!employee || !employee.studio || !Array.isArray(employee.studio) || !studioContext) return {}
      return employee.studio.find((std) => std._id === studioContext._id || std.team === studioContext._id) || {}
    },

    openAddEmployeeDialog(studio) {
      this.currentStudioForAdd = studio
      this.newEmployeeData = this.getInitialNewEmployeeData() // Wichtig: Zurücksetzen bei jedem Öffnen
      this.addEmployeeDialog = true
    },
    closeAddEmployeeDialog() {
      this.addEmployeeDialog = false
      this.currentStudioForAdd = null
      this.newEmployeeData = this.getInitialNewEmployeeData() // Auch beim Schliessen zurücksetzen
      this.isSavingNewEmployee = false
    },
    async pickUserForAdd(user_id) {
      if (!user_id) {
        this.newEmployeeData = this.getInitialNewEmployeeData()
        return
      }
      try {
        // API-Aufruf, um User-Details zu holen (angenommen, user_id ist die _id des Users)
        const response = await api.getUserById(user_id)
        const pickedUser = response.user

        if (!pickedUser) {
          this.$toast.error("Benutzer nicht gefunden.")
          this.newEmployeeData = this.getInitialNewEmployeeData()
          return
        }

        // Prüfen, ob der User bereits diesem spezifischen Studio/Team zugewiesen ist
        const isAlreadyInThisStudioTeam = this.employee.some(
          (emp) =>
            emp.userId === pickedUser._id && // Vergleiche mit der eindeutigen User-ID
            emp.studio &&
            emp.studio.some(
              (s) =>
                (s._id && this.currentStudioForAdd && s._id === this.currentStudioForAdd._id) ||
                (s.team && this.currentStudioForAdd && s.team === this.currentStudioForAdd._id),
            ),
        )

        if (isAlreadyInThisStudioTeam) {
          this.$toast.info(`${pickedUser.vorname} ${pickedUser.nachname} ist bereits in "${this.currentStudioForAdd.name}".`)
          // User-Suche ggf. wieder ermöglichen, indem newEmployeeData zurückgesetzt wird
          // this.newEmployeeData = this.getInitialNewEmployeeData();
          return
        }

        this.newEmployeeData = {
          _id: pickedUser._id, // Die _id des User-Objekts
          userId: pickedUser._id, // Zur Sicherheit, falls Backend `userId` erwartet
          userEmail: pickedUser.email,
          fullName: `${pickedUser.vorname} ${pickedUser.nachname}`,
          picture: pickedUser.picture || null,
        }
      } catch (e) {
        console.error("Error picking user:", e)
        this.$toast.error("Fehler beim Auswählen des Benutzers.")
        this.newEmployeeData = this.getInitialNewEmployeeData() // Zurücksetzen im Fehlerfall
      }
    },
    // In methods:
    async saveNewEmployee() {
      if (!this.newEmployeeData._id || !this.currentStudioForAdd) {
        this.$toast.error("Benutzer und Studio/Team müssen ausgewählt sein.")
        return
      }
      this.isSavingNewEmployee = true

      // Payload soll dem alten Schema ähneln:
      // Das Objekt enthält die User-Daten und direkt eine 'studio' ODER 'team' ID.
      const payload = {
        _id: this.newEmployeeData._id, // Die _id des User-Objekts (wird oft als Haupt-ID des Employees verwendet)
        userId: this.newEmployeeData._id, // Ggf. redundant, aber sicher ist sicher, falls Backend explizit userId erwartet
        fullName: this.newEmployeeData.fullName, // Mitgeben, falls API es nicht selbst vom User-Datensatz zieht
        email: this.newEmployeeData.userEmail, // Mitgeben
        userEmail: this.newEmployeeData.userEmail, // Mitgeben, falls API es erwartet
        picture: this.newEmployeeData.picture, // Mitgeben
      }

      // Studio-/Team-ID direkt im Payload setzen, wie in deinem alten Code
      if (this.currentStudioForAdd.code) {
        // Es ist ein richtiges Studio (hat einen 'code')
        payload.studio_id = this.currentStudioForAdd._id // Expliziter Name, um Kollision mit 'studio'-Array zu vermeiden
        // oder einfach `payload.studio = this.currentStudioForAdd._id;`
        // wenn die API das versteht und kein 'studio'-Array erwartet.
        // Ich verwende hier `studio_id` um klar zu machen, dass es die ID ist.
        // Dein alter Code nutzte `payload.studio`. Ich folge dem mal:
        payload.studio = this.currentStudioForAdd._id
        // delete payload.team; // Falls 'team' Property existiert und stört
      } else {
        // Es ist ein Team
        payload.team = this.currentStudioForAdd._id
        // delete payload.studio; // Falls 'studio' Property existiert und stört
      }

      // WICHTIG:
      // Der obige Payload sendet nun `title`, `percentage` etc. auf der Root-Ebene.
      // Die Backend-API `createEmployee` muss:
      // 1. Wenn der Employee (basierend auf `userId` oder `_id`) noch nicht existiert:
      //    - Einen neuen Employee-Datensatz anlegen.
      //    - Das `payload.studio` (die ID) oder `payload.team` (die ID) verwenden, um eine *erste*
      //      Studio/Team-Zuweisung im (intern zu erstellenden) `employee.studio`-Array zu erstellen.
      //    - Die Root-Level-Felder wie `title`, `percentage`, `sort` etc. aus dem Payload
      //      in dieses *erste* Element des `employee.studio`-Arrays kopieren.
      // 2. Wenn der Employee bereits existiert:
      //    - Das `payload.studio` (ID) oder `payload.team` (ID) verwenden, um eine *weitere*
      //      Studio/Team-Zuweisung zum `employee.studio`-Array hinzuzufügen.
      //    - Für diese *neue* Zuweisung muss die API ebenfalls die Root-Level-Felder
      //      `title`, `percentage` etc. aus dem Payload als Default-Werte verwenden.
      //    - Alternativ (und sauberer) wäre ein separater API-Endpunkt, um einen bestehenden
      //      Mitarbeiter einem weiteren Studio/Team hinzuzufügen, der dann die Studio-Details explizit entgegennimmt.

      try {
        const savedOrUpdatedEmployee = await api.createEmployee(payload)

        if (savedOrUpdatedEmployee && savedOrUpdatedEmployee._id) {
          // Annahme: `savedOrUpdatedEmployee` ist das volle Employee-Objekt,
          // inklusive eines Arrays `studio`, das alle Zuweisungen enthält (auch die neu hinzugefügte).
          const existingEmployeeIndex = this.employee.findIndex((e) => e._id === savedOrUpdatedEmployee._id)

          if (existingEmployeeIndex > -1) {
            // Mitarbeiter existiert bereits, ersetze ihn mit der aktualisierten Version
            this.$set(this.employee, existingEmployeeIndex, savedOrUpdatedEmployee)
          } else {
            // Mitarbeiter ist komplett neu in der lokalen Liste
            this.employee.push(savedOrUpdatedEmployee)
          }
          this.$toast.success(
            `${savedOrUpdatedEmployee.fullName || this.newEmployeeData.fullName} wurde zu ${this.currentStudioForAdd.name} hinzugefügt.`,
          )
          this.closeAddEmployeeDialog()
        } else {
          // Fallback, falls die API nicht das erwartete Objekt zurückgibt.
          this.$toast.error("Mitarbeiter konnte nicht hinzugefügt werden: Unerwartete Antwort der API.")
          // Evtl. Daten neu laden, um Konsistenz sicherzustellen.
          // await this.fetchEmployees();
        }
      } catch (error) {
        console.error("Error saving new employee:", error)
        let errorMessage = "Fehler beim Speichern des Mitarbeiters."
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }
        this.$toast.error(errorMessage)
      } finally {
        this.isSavingNewEmployee = false
      }
    },

    openEditEmployeeDialog(employee, studioContext) {
      this.selectedEmployeeForEdit = JSON.parse(JSON.stringify(employee))
      this.currentStudioContext = studioContext
      const studioData = this.getEmployeeStudioData(this.selectedEmployeeForEdit, studioContext)

      this.selectedEmployeeStudioData = JSON.parse(JSON.stringify(studioData || {}))

      // Fallback: search for any non-empty pensumHistory across all studios if the current one has none
      if (!this.selectedEmployeeStudioData.pensumHistory || this.selectedEmployeeStudioData.pensumHistory.length === 0) {
        const otherStudioWithHistory = this.selectedEmployeeForEdit.studio.find((s) => s.pensumHistory && s.pensumHistory.length > 0)
        if (otherStudioWithHistory) {
          this.selectedEmployeeStudioData.pensumHistory = JSON.parse(JSON.stringify(otherStudioWithHistory.pensumHistory))
        }
      }

      // Standardwerte für selectedEmployeeStudioData, falls nicht vorhanden
      const defaults = {
        title: "",
        percentage: 0,
        vacationHours: 0,
        border: null,
        centerManager: false,
        centerManagerEmail: "",
        sort: 0,
        mainStudio: false,
        hide: false,
        showInCalendarAnyway: false,
        pensumHistory: [],
      }
      for (const key in defaults) {
        if (typeof this.selectedEmployeeStudioData[key] === "undefined") {
          this.$set(this.selectedEmployeeStudioData, key, defaults[key])
        }
      }
      this.$set(this.selectedEmployeeStudioData, "centerManager", this.selectedEmployeeStudioData.centerManager || false)
      // Konvertiere zu Zahlen wo nötig
      this.selectedEmployeeStudioData.percentage = parseFloat(this.selectedEmployeeStudioData.percentage) || 0
      this.selectedEmployeeStudioData.vacationHours = parseFloat(this.selectedEmployeeStudioData.vacationHours) || 0
      this.selectedEmployeeStudioData.sort = parseInt(this.selectedEmployeeStudioData.sort) || 0

      this.showSlimCropper = true // SlimCropper direkt anzeigen
      this.editEmployeeDialog = true
    },
    closeEditEmployeeDialog() {
      this.editEmployeeDialog = false
      this.selectedEmployeeForEdit = null
      this.currentStudioContext = null
      this.selectedEmployeeStudioData = {}
      this.showSlimCropper = false
    },
    // In methods:
    async saveEditedEmployee() {
      if (!this.selectedEmployeeForEdit || !this.currentStudioContext) return

      // Schritt 1: Stelle sicher, dass alle lokalen Änderungen in selectedEmployeeForEdit korrekt sind.
      // (Konvertierungen zu Zahlen, Zusammenführen von selectedEmployeeStudioData)
      const studioIndex = this.selectedEmployeeForEdit.studio.findIndex(
        (std) => (std._id && std._id === this.currentStudioContext._id) || (std.team && std.team === this.currentStudioContext._id),
      )

      this.selectedEmployeeStudioData.percentage = parseFloat(this.selectedEmployeeStudioData.percentage) || 0
      this.selectedEmployeeStudioData.vacationHours = parseFloat(this.selectedEmployeeStudioData.vacationHours) || 0
      this.selectedEmployeeStudioData.sort = parseInt(this.selectedEmployeeStudioData.sort) || 0

      if (!this.selectedEmployeeStudioData.hide) {
        this.selectedEmployeeStudioData.showInCalendarAnyway = false
      }

      if (studioIndex !== -1) {
        const originalStd = this.selectedEmployeeForEdit.studio[studioIndex]
        // Erstelle eine *neue* Studio-Array-Referenz, um die Reaktivität zu fördern
        const newStudioArray = [...this.selectedEmployeeForEdit.studio]
        newStudioArray[studioIndex] = {
          ...originalStd,
          ...this.selectedEmployeeStudioData,
        }

        // Synchronisiere die pensumHistory auf alle Studios des Mitarbeiters
        const currentPensumHistory = this.selectedEmployeeStudioData.pensumHistory || []
        newStudioArray.forEach((std) => {
          std.pensumHistory = JSON.parse(JSON.stringify(currentPensumHistory))
        })

        this.selectedEmployeeForEdit.studio = newStudioArray // Weise das neue Array zu
      } else {
        console.error("Studio context not found in employee's studio list for update.")
        this.$toast.error("Fehler: Studio-Kontext für Update nicht gefunden.")
        return
      }

      this.selectedEmployeeForEdit.timeTrackerPercentage = parseFloat(this.selectedEmployeeForEdit.timeTrackerPercentage) || null
      this.selectedEmployeeForEdit.overTime = parseFloat(this.selectedEmployeeForEdit.overTime) || 0

      // Erstelle eine tiefe Kopie des Objekts, das wir in die Liste einfügen wollen, *bevor* die API antwortet.
      // Dies ist der Zustand, den wir auf jeden Fall im UI sehen wollen.
      const locallyUpdatedEmployee = JSON.parse(JSON.stringify(this.selectedEmployeeForEdit))

      try {
        const returnedEmployeeFromAPI = await api.updateEmployee(this.selectedEmployeeForEdit) // Sende die Änderungen an die API

        // Nun aktualisiere die lokale Liste.
        // `locallyUpdatedEmployee` enthält alle lokalen Änderungen.
        // `returnedEmployeeFromAPI` enthält die Antwort der API (kann identisch sein, oder z.B. mit `updatedAt` Zeitstempel).
        // Wir nehmen `locallyUpdatedEmployee` als Basis und überschreiben/ergänzen mit Feldern aus `returnedEmployeeFromAPI`.

        const finalEmployeeState = {
          ...locallyUpdatedEmployee,
          ...returnedEmployeeFromAPI, // Felder von der API (z.B. _id, updatedAt) überschreiben/ergänzen
          // Stelle sicher, dass `returnedEmployeeFromAPI` nicht versehentlich
          // korrekte Daten in `locallyUpdatedEmployee.studio` überschreibt, falls
          // `returnedEmployeeFromAPI.studio` nicht aktuell/vollständig ist.
          // Wenn `returnedEmployeeFromAPI.studio` vertrauenswürdig ist:
          // studio: returnedEmployeeFromAPI.studio || locallyUpdatedEmployee.studio
        }
        // Um sicherzugehen, dass das Studio-Array das aktuellste ist:
        finalEmployeeState.studio = locallyUpdatedEmployee.studio

        const index = this.employee.findIndex((emp) => emp._id === finalEmployeeState._id)
        if (index !== -1) {
          // Wichtig: Ersetze das Objekt in der Liste, um Vue zur Aktualisierung zu zwingen.
          this.employee.splice(index, 1, finalEmployeeState)
          // this.$set(this.employee, index, finalEmployeeState); // Alternative zu splice
        } else {
          // Sollte nicht passieren, wenn Mitarbeiter bereits existiert
          this.employee.push(finalEmployeeState) // Fallback
        }

        this.snackbar = true
        this.closeEditEmployeeDialog()
      } catch (error) {
        console.error("Error updating employee:", error)
        this.$toast.error(`Fehler beim Aktualisieren: ${error.message || "Unbekannter Fehler"}`)
      }
    },
    async confirmDeleteEmployee(employee, studioOrTeamId) {
      if (confirm(`Möchten Sie ${employee.fullName} wirklich aus "${this.currentStudioContext.name}" entfernen?`)) {
        try {
          const result = await api.deleteEmployee(employee._id, studioOrTeamId)
          if (
            result.ok === 1 ||
            result.success ||
            result.deletedCount > 0 ||
            result.modifiedCount > 0 ||
            result.message === "Employee removed from studio/team"
          ) {
            const empIndex = this.employee.findIndex((e) => e._id === employee._id)
            if (empIndex !== -1) {
              const stdIndex = this.employee[empIndex].studio.findIndex(
                (s) => (s._id && s._id === studioOrTeamId) || (s.team && s.team === studioOrTeamId),
              )
              if (stdIndex !== -1) {
                this.employee[empIndex].studio.splice(stdIndex, 1)
                if (this.employee[empIndex].studio.length === 0) {
                  this.employee.splice(empIndex, 1)
                }
                this.$forceUpdate()
              }
            }
            this.$toast.success(`${employee.fullName} wurde entfernt.`)
            this.closeEditEmployeeDialog()
          } else {
            this.$toast.error("Mitarbeiter konnte nicht entfernt werden. Antwort der API: " + JSON.stringify(result))
          }
        } catch (e) {
          console.error(e)
          this.$toast.error(`Fehler beim Löschen: ${e.message || "Unbekannter Fehler"}`)
        }
      }
    },

    async downloadBLSAEDFile(fileName) {
      if (!fileName) {
        this.$toast.info("Keine Datei zum Download vorhanden.")
        return
      }
      try {
        await api.downloadBLSAEDFile(fileName)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Download der BLS-AED Datei.")
      }
    },
    async downloadBLSListFileZip() {
      try {
        this.$toast.info("ZIP Download wird vorbereitet...")
        await api.downloadBLSAEDZip()
        // Download wird serverseitig ausgelöst, kein Toast success hier, da Browser den Download anzeigt
      } catch (e) {
        this.$toast.error("Fehler beim Download des ZIP Ordners")
      }
    },
    async onBLSFileChange(event, emp) {
      let selectedFile = event.target.files[0]
      if (!selectedFile) {
        // this.$toast.info('Keine Datei ausgewählt.'); // Nicht nötig, da kein Fehler
        return
      }
      const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"]
      if (!allowedTypes.includes(selectedFile.type)) {
        this.$toast.error("Ungültiger Dateityp. Nur PDF, JPG, PNG erlaubt.")
        event.target.value = null // Input zurücksetzen
        return
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        // 5MB Limit
        this.$toast.error("Datei ist zu gross (max. 5MB).")
        event.target.value = null // Input zurücksetzen
        return
      }

      let newFileName = `${emp.fullName}_BLS-AED_${this.$moment().format("YYYY-MM-DD")}.${selectedFile.name.split(".").pop()}`
      newFileName = newFileName.replace(/[^a-zA-Z0-9_.-]/g, "_") // Sonderzeichen ersetzen

      let newFile = new File([selectedFile], newFileName, {
        type: selectedFile.type,
        lastModified: selectedFile.lastModified,
      })

      const formData = new FormData()
      formData.append("file", newFile)
      formData.append("user", emp.userId || emp._id)

      try {
        this.$toast.info("Datei wird hochgeladen...")
        const response = await api.uploadBLSAEDFile(formData)
        if (response && response.name) {
          if (this.selectedEmployeeForEdit && this.selectedEmployeeForEdit._id === emp._id) {
            this.selectedEmployeeForEdit.blsAedFile = response.name
          } else {
            // Fallback, falls der Mitarbeiter nicht im Dialog ist (sollte nicht passieren)
            emp.blsAedFile = response.name
          }
          this.$toast.success("BLS-AED Zertifikat erfolgreich hochgeladen!")
        } else {
          this.$toast.error("Fehler: Ungültige Antwort vom Server nach Upload.")
        }
      } catch (error) {
        console.error(error)
        this.$toast.error(`Upload-Fehler: ${error.message || "Unbekannter Fehler"}`)
      }
      event.target.value = null
    },
    triggerBLSFileInput(event, emp, refPrefix = "fileInput") {
      const refName = `${refPrefix}${emp._id}`
      if (this.$refs[refName] && this.$refs[refName][0]) {
        this.$refs[refName][0].click()
      } else if (this.$refs[refName]) {
        this.$refs[refName].click()
      } else {
        console.error(`File input ref '${refName}' nicht gefunden.`)
        this.$toast.error("Dateiauswahl konnte nicht geöffnet werden.")
      }
    },
    async downloadBLSList() {
      try {
        this.$toast.info("BLS Liste wird generiert...")
        await api.downloadBLSList()
      } catch (e) {
        this.$toast.error("Fehler beim Herunterladen der BLS Liste")
      }
    },
    getPercentageForEmployee(employee, studioContext) {
      if (!employee) return 0
      const empStudioData = this.getEmployeeStudioData(employee, studioContext)
      let pensumHistory = (empStudioData && empStudioData.pensumHistory) || []

      if (!pensumHistory.length && employee.studio && employee.studio.length > 0) {
        const withHistory = employee.studio.find((s) => s.pensumHistory && s.pensumHistory.length > 0)
        if (withHistory) {
          pensumHistory = withHistory.pensumHistory
        }
      }

      // 1. NEU: Zuerst in der Pensum-Historie prüfen
      if (pensumHistory && pensumHistory.length > 0) {
        let targetFormat = this.$moment().format("YYYY-MM")
        let history = pensumHistory.slice()

        // Absteigend sortieren (neueste Gültigkeit zuerst)
        let sortedHistory = history.sort((a, b) => b.validFrom.localeCompare(a.validFrom))

        // Den aktuellsten Eintrag finden, der bereits gültig ist (<= aktueller Monat)
        let applicable = sortedHistory.find((h) => h.validFrom && h.validFrom <= targetFormat)

        if (applicable && applicable.percentage !== undefined && applicable.percentage !== null) {
          const histPercentage = parseFloat(applicable.percentage)
          if (!isNaN(histPercentage) && histPercentage > 0) {
            return histPercentage
          }
        }
      }

      // 2. Fallback: timeTrackerPercentage
      const ttPercentage = parseFloat(employee.timeTrackerPercentage)
      if (employee.timeTrackerPercentage && !isNaN(ttPercentage) && ttPercentage > 0) {
        return ttPercentage
      }

      // 3. Fallback: Default to 100%
      return 100
    },
    applyCalculatedVacation() {
      let percentage = this.getPercentageForEmployee(this.selectedEmployeeForEdit, this.currentStudioContext)
      if (percentage <= 0) return this.$toast.error("Bitte zuerst das Pensum eintragen, um die Ferien sicher zu berechnen.")
      if (this.calculatedVacationDays > 0) {
        let hours = Math.round(((this.calculatedVacationDays * 8.4 * percentage) / 100) * 100) / 100
        this.selectedEmployeeStudioData.vacationHours = hours
        this.$toast.success("Ferien-Saldo wurde berechnet.")
      }
    },
    async deleteTeam(teamId) {
      const teamToDelete = this.studios.find((s) => s._id === teamId && !s.code)
      if (!teamToDelete) {
        this.$toast.error("Team nicht gefunden.")
        return
      }

      let teamMembers = this.employee.filter(
        (emp) => emp.studio && emp.studio.find((studio) => studio.team && studio.team.toString() === teamId.toString()),
      )
      if (teamMembers.length > 0) {
        this.$toast.error(`Team "${teamToDelete.name}" kann nicht gelöscht werden, da noch ${teamMembers.length} Mitarbeiter zugeordnet sind.`)
        return
      }
      if (confirm(`Möchten Sie das Team "${teamToDelete.name}" wirklich löschen? Alle Einstellungen für dieses Team gehen verloren.`)) {
        try {
          await api.deleteTeam(teamId)
          this.$toast.success(`Team "${teamToDelete.name}" gelöscht.`)
          this.$emit("refreshData") // Elternkomponente soll Studios neu laden
        } catch (e) {
          this.$toast.error("Fehler beim Löschen des Teams.")
          console.error(e)
        }
      }
    },
    vacationDaysForMonth(studio) {
      if (!studio.vacationDays || !studio.vacationDays.length) return []
      return studio.vacationDays.filter(
        (day) => this.$moment(day.date).isValid() && this.$moment().format("YYYY-MM") === this.$moment(day.date).format("YYYY-MM"),
      )
    },
    async createNewTeam() {
      if (!this.newTeam.name || !this.newTeam.studio) {
        this.$toast.error("Teamname und zugehöriges Center müssen ausgewählt werden.")
        return
      }
      try {
        await api.createTeam(this.newTeam)
        this.$toast.success(`Team "${this.newTeam.name}" wurde erstellt.`)
        this.$emit("refreshData") // Elternkomponente soll Studios neu laden
        this.newTeam = { name: "", studio: null, targetHours: [] }
      } catch (e) {
        this.$toast.error("Fehler beim Erstellen des Teams.")
        console.error(e)
      }
    },
    employeeForStudio(studioContext) {
      if (!this.employee || !Array.isArray(this.employee)) return []
      return this.employee
        .filter(
          (emp) =>
            emp.studio &&
            Array.isArray(emp.studio) &&
            emp.studio.find((std) => {
              return (
                (std._id && studioContext._id && std._id.toString() === studioContext._id.toString()) ||
                (std.team && studioContext._id && std.team.toString() === studioContext._id.toString())
              )
            }),
        )
        .sort((a, b) => {
          const empStudioDataA = this.getEmployeeStudioData(a, studioContext) || {}
          const empStudioDataB = this.getEmployeeStudioData(b, studioContext) || {}

          let sortA = parseInt(empStudioDataA.sort)
          let sortB = parseInt(empStudioDataB.sort)

          if (isNaN(sortA)) sortA = 0
          if (isNaN(sortB)) sortB = 0

          if (sortA === 0 && sortB !== 0) {
            return 1
          }
          if (sortB === 0 && sortA !== 0) {
            return -1
          }
          if (sortA === sortB) {
            return (a.fullName || "").localeCompare(b.fullName || "")
          }
          return sortA - sortB
        })
    },
    uploadPicture(formdata, progress, success, failure, slimInstance) {
      if (!this.selectedEmployeeForEdit || !this.selectedEmployeeForEdit._id) {
        failure("Kein Mitarbeiter ausgewählt für Bild-Upload.")
        return
      }
      const file = formdata[0] // formdata ist hier das File-Objekt

      const formData = new FormData()
      formData.append("file", file)

      let uploadimg = {}
      uploadimg.art = "pfp"
      uploadimg._id = this.selectedEmployeeForEdit._id
      uploadimg.formData = formData

      this.$toast.info("Profilbild wird hochgeladen...")
      api
        .uploadImage(uploadimg)
        .then((data) => {
          if (data.status && data.name) {
            this.selectedEmployeeForEdit.picture = data.name
            const empInList = this.employee.find((e) => e._id === this.selectedEmployeeForEdit._id)
            if (empInList) {
              empInList.picture = data.name
            }
            success(data.name)
            this.$toast.success("Profilbild aktualisiert.")
          } else {
            failure("Fehlerhafte Antwort vom Server: " + (data.message || "Unbekannter Fehler"))
            this.$toast.error("Profilbild konnte nicht hochgeladen werden.")
          }
        })
        .catch((error) => {
          console.error("Error uploading image:", error)
          failure("Upload fehlgeschlagen: " + error.message)
          this.$toast.error(`Upload-Fehler: ${error.message || "Unbekannter Fehler"}`)
        })
    },
    otherStudiosForEmployee(employee, currentContext) {
      if (!employee || !employee.studio || !Array.isArray(employee.studio)) return []
      return employee.studio.filter((empStudio) => {
        if (currentContext.code) {
          // Aktueller Kontext ist ein Studio
          // Zeige alle anderen Teams ODER andere Studios
          return empStudio.team || (empStudio._id && empStudio._id !== currentContext._id)
        } else {
          // Aktueller Kontext ist ein Team
          // Zeige alle Studios ODER andere Teams
          return empStudio._id || (empStudio.team && empStudio.team !== currentContext._id)
        }
      })
    },
    getStudioOrTeamName(studioOrTeamEntry) {
      if (!this.storeStudios || !Array.isArray(this.storeStudios)) return "N/A"
      if (studioOrTeamEntry.team) {
        const teamObj = this.storeStudios.find((s) => s._id === studioOrTeamEntry.team)
        return teamObj ? teamObj.name : "Unbekanntes Team"
      }
      if (studioOrTeamEntry._id) {
        const studioObj = this.storeStudios.find((s) => s._id === studioOrTeamEntry._id)
        return studioObj ? studioObj.name : "Unbekanntes Studio"
      }
      return "N/A"
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    hasActivePensumOverride() {
      return this.activePensumOverride !== null
    },
    activePensumOverride() {
      if (
        !this.selectedEmployeeStudioData ||
        !this.selectedEmployeeStudioData.pensumHistory ||
        this.selectedEmployeeStudioData.pensumHistory.length === 0
      )
        return null
      let targetFormat = this.$moment().format("YYYY-MM")
      let history = this.selectedEmployeeStudioData.pensumHistory.slice()
      let sortedHistory = history.sort((a, b) => b.validFrom.localeCompare(a.validFrom))
      let applicable = sortedHistory.find((h) => h.validFrom && h.validFrom <= targetFormat)
      return applicable ? applicable : null
    },
    calculatedVacationDays() {
      if (!this.selectedEmployeeForEdit || !this.selectedEmployeeForEdit.yearlyVacationDays) return 0
      let days = parseFloat(this.selectedEmployeeForEdit.yearlyVacationDays) || 0
      let entryDate = this.selectedEmployeeForEdit.entryDate ? this.$moment(this.selectedEmployeeForEdit.entryDate, "YYYY-MM-DD") : null
      let exitDate = this.selectedEmployeeForEdit.exitDate ? this.$moment(this.selectedEmployeeForEdit.exitDate, "YYYY-MM-DD") : null
      let currentYear = this.$moment().year()

      let startMonth = 1
      let endMonth = 12

      if (entryDate && entryDate.year() === currentYear) {
        startMonth = entryDate.month() + 1
      } else if (entryDate && entryDate.year() > currentYear) {
        return 0
      }

      if (exitDate && exitDate.year() === currentYear) {
        endMonth = exitDate.month() + 1
      } else if (exitDate && exitDate.year() < currentYear) {
        return 0
      }

      let months = endMonth - startMonth + 1
      if (months <= 0) return 0
      if (months === 12) return days

      return Math.round((days / 12) * months * 100) / 100
    },
    calcBusinessDays() {
      var day = this.$moment().startOf("month")
      var businessDays = 0
      var month = day.month()
      while (day.month() === month) {
        // Sicherstellen, dass wir im aktuellen Monat bleiben
        if (day.day() !== 0 && day.day() !== 6) businessDays++
        day.add(1, "d")
      }
      return businessDays
    },
    slimOptions() {
      return {
        ratio: "83:92", // Zurück zum Originalwert

        buttonCancelLabel: "Abbrechen",
        buttonConfirmLabel: "Bestätigen",
        buttonEditLabel: "Bearbeiten",
        buttonRemoveLabel: "Entfernen", // Falls Bild entfernt werden soll
        label: '<p class="text-caption"><i class="mdi mdi-camera-plus-outline" style="font-size: 20px;"></i><br>Neues Bild</p>',
        labelLoading: "Bild wird verarbeitet...",
        statusUploadSuccess: "Gespeichert!",
        statusUnknownResponse: "Serverantwort nicht erkannt.",
        statusFileSize: "Datei ist zu gross (max. 2MB).",
        statusContentLength: "Fehler beim Senden der Datei.",
        statusNoResponse: "Server nicht erreichbar.",
        statusIMAGE_TOO_SMALL: "Bild ist zu klein. Min. 166x184 Pixel.",

        service: this.uploadPicture,
        serviceFormat: "file",
        instantEdit: true, // Startet nicht sofort den Editor, sondern zeigt erst den Upload-Button
        push: true,
        // initialImage wird dynamisch im Template gesetzt oder hier, wenn selectedEmployeeForEdit verfügbar ist
        // initialImage: (this.selectedEmployeeForEdit && this.selectedEmployeeForEdit.picture)
        //     ? `/img/profilbilder/${this.selectedEmployeeForEdit.picture}`
        //     : '/img/einmalig/imageUploadPlaceholderPFP.jpg', // Wird im Template behandelt

        didInit: (data, slim) => {
          // console.log('Slim initialized');
        },
        didLoad: (file, image, meta) => {
          // console.log('Image loaded', file.name, image.width, image.height, meta);
          return true
        },
        didSave: (file, response) => {
          if (response && typeof response === "string") {
            this.selectedEmployeeForEdit.picture = response
          } else if (response && response.name) {
            this.selectedEmployeeForEdit.picture = response.name
          }
          // Wichtig: Cropper nicht hier schliessen, falls User noch andere Felder bearbeiten will
          // this.showSlimCropper = false; // Nur schliessen, wenn es eine separate "Fertig" Aktion gibt
        },
        didCancel: () => {
          // Wird aufgerufen, wenn der Benutzer im Slim-Editor auf "Abbrechen" klickt
          // this.showSlimCropper = false; // Schliesst den Cropper-Bereich
        },
        didRemove: (file, data) => {
          // Logik, wenn Bild entfernt wird (z.B. API Call um Bild zu löschen)
          // this.selectedEmployeeForEdit.picture = null;
          // api.deleteEmployeeImage(this.selectedEmployeeForEdit._id);
        },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.blue-border {
  border: 5px solid #2196f3 !important; // Vuetify blue
}
.green-border {
  border: 5px solid #4caf50 !important; // Vuetify green
}
.gold-border {
  border: 5px solid #ffc107 !important; // Vuetify amber/gold
}

.employee-manager {
  &__card {
    min-height: 165px;
    position: relative;
    padding-top: 70px;
    cursor: pointer;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
    }
  }
  &__card-avatar {
    position: absolute;
    top: -62px;
    left: 50%;
    transform: translateX(-50%);
    // box-shadow: 0 4px 12px rgba(0,0,0,0.3); // Reduziert, da Karte schon Schatten hat
    border: 2px solid rgba(255, 255, 255, 0.5); // Heller Rand um den Avatar für besseren Kontrast auf dunkler Karte
  }
  &__card-avatar-dialog {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4); // Etwas stärkerer Schatten im dunklen Dialog
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}

// Stellt sicher, dass der Slim Cropper korrekt angezeigt wird
::v-deep .slim-image-editor {
  // Wird relativ zum Elternelement positioniert
  z-index: 10; // Innerhalb des Dialogs
}
::v-deep .slim-wrapper {
  // border-radius: 8px; // Leicht abgerundete Ecken für den Cropper selbst, passend zur Ratio
  // Die Ratio bestimmt die Form, border-radius rundet die Ecken davon ab.
}
::v-deep .slim-label {
  font-size: 0.75rem;
  text-align: center;
}
::v-deep .slim-label p {
  margin-bottom: 0.2rem;
}
::v-deep .slim-button-cancel,
::v-deep .slim-button-confirm {
  font-size: 0.8rem !important;
  padding: 6px 10px !important;
}
::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.25) !important;
}
::v-deep .v-select--outlined .v-select__selections {
  color: white;
}
::v-deep .v-input--is-disabled .v-input__control > .v-input__slot {
  background-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
